import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useToast } from '@platform/ui';

import {
  PatientDetailError,
  PatientDetailLoading,
  PatientDetailView,
} from '../../features/patients/patient-detail';
import { useOperationsClient } from '../../lib/api/operations-client-context';
import { usePaymentsClient } from '../../lib/api/payments-client-context';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';
import { describeApiError } from '../../lib/session/describe-error';
import { useStepUp } from '../../lib/session/step-up';

export const Route = createFileRoute('/patients/$patientId')({
  component: PatientDetailPage,
});

const CLINIC_ZONE = 'America/Los_Angeles';

/** `/patients/$patientId` — the patient record (MASTER_PLAN M11.3, Docs/API.md §2.6). */
function PatientDetailPage() {
  const { patientId } = Route.useParams();
  const client = useOperationsClient();
  /* Removing a card is a payments action on the real API (M7.8), not an operations one. */
  const payments = usePaymentsClient();
  const scheduling = useSchedulingClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { guard } = useStepUp();

  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const patient = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => client.getPatient(patientId),
  });
  const settings = useQuery({
    queryKey: ['scheduling-settings'],
    queryFn: () => scheduling.getSettings(),
  });

  /* `GET /admin/patients/:id/audit` is `patients.read`, not `audit.read`: it is this
     patient's own trail, reached from their record (API.md §2.6). Cursor paginated, so
     "Load older entries" carries the cursor forward rather than asking for a bigger page. */
  const audit = useInfiniteQuery({
    queryKey: ['patient-audit', patientId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      client.listPatientAudit(patientId, { limit: 25, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const run = useMutation({
    mutationFn: async ({ action }: { action: () => Promise<unknown>; message: string }) => {
      await action();
    },
    onSuccess: async (_result, variables) => {
      setActionError(undefined);
      toast({ tone: 'success', message: variables.message });
      await queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      await queryClient.invalidateQueries({ queryKey: ['patient-audit', patientId] });
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
    onError: (error: Error) => {
      const message = describeApiError(error);
      setActionError(message);
      toast({ tone: 'error', message });
    },
  });

  const act = (message: string, action: () => Promise<unknown>): void => {
    run.mutate({ action, message });
  };

  if (patient.isPending) return <PatientDetailLoading />;
  if (patient.isError) return <PatientDetailError message={describeApiError(patient.error)} />;

  return (
    <PatientDetailView
      detail={patient.data}
      clinicTimeZone={settings.data?.timeZone ?? CLINIC_ZONE}
      busy={run.isPending}
      actionError={actionError}
      audit={audit.data?.pages.flatMap((page) => page.data) ?? []}
      auditLoading={audit.isPending}
      auditError={audit.isError ? describeApiError(audit.error) : undefined}
      onLoadMoreAudit={
        audit.hasNextPage
          ? () => {
              void audit.fetchNextPage();
            }
          : undefined
      }
      auditLoadingMore={audit.isFetchingNextPage}
      onSaveContact={(patch) => {
        act('Contact details saved.', () => client.updatePatientContact(patientId, patch));
      }}
      onRemovePaymentMethod={(paymentMethodId) => {
        act('Card removed.', () => payments.removePaymentMethod(patientId, paymentMethodId));
      }}
      onRequestAnonymization={(input) => {
        /*
         * Step-up guarded (SECURITY.md §5.1): `patients.anonymize` is a clinic-admin key
         * *and* a second factor, and `guard` re-runs this same call once the prompt is
         * answered rather than making the person retype the reason and the phrase.
         */
        act('Record anonymized.', () =>
          guard(() => client.requestAnonymization(patientId, input), 'anonymize this record'),
        );
      }}
      renderLink={(to, children, className): ReactNode => (
        <Link to={to} className={className}>
          {children}
        </Link>
      )}
    />
  );
}
